import { useAtom } from 'jotai';
import { createContext, ReactNode, useEffect, useState } from 'react';
import { AvailableAvpViews } from '~/app/compositions/content/gallery/avp/types';
import { AvpConfigIds } from '~/app/market-config';
import { CarWithDealer } from '~/app/pages/configurator/components/configurator-option/models/stock-match';
import { CtxConfiguratorType } from '~/app/pages/configurator/models/ctx-configurator-type';
import { state } from '~/storage';

export const CtxConfigurator = createContext<CtxConfiguratorType | undefined>(undefined);

export function CtxConfiguratorProvider({ children }: { children: ReactNode }) {
	const [isThumbnailOpen, setIsThumbnailOpen] = useState(false);
	const [galleryWidth, setGalleryWidth] = useState(0);
	const [galleryHeight, setGalleryHeight] = useState(0);
	const [galleryTop, setGalleryTop] = useState(0);
	const [optionHeaderHeight, setOptionHeaderHeight] = useState(0);
	const [triggerComparisonPosition, setTriggerComparisonPosition] = useState<string>();
	const [isPinned, setIsPinned] = useState(false);
	const [hasAVPError, setHasAVPError] = useState(false);
	const [totalMatchFound, setTotalMatchFound] = useState(0);
	const [carWithDealer, setCarWithDealer] = useState<CarWithDealer | undefined>();
	const [active3DViews, setActive3DViews] = useAtom(state.active3DViews);
	const [AVPSessionId, setAVPSessionId] = useAtom(state.AVPSessionId);
	const [AVPConfigId, setAVPConfigId] = useAtom<AvpConfigIds>(state.AVPConfigId);
	const [AVPCameraId, setAVPCameraId] = useAtom(state.AVPCameraId);
	// used to save the session id before it can be passed to al lthe other instances
	const [buffer, setBuffer] = useState('');
	const [hideAVP, setHideAVP] = useState(false);
	const [openPriceDetails, setOpenPriceDetails] = useState(false);

	const connectedToAVP = (sessionId: string) => {
		if (!AVPSessionId && active3DViews.length === 1) {
			setBuffer(sessionId);
		}
	};

	const handleException = (event: ExceptionEvent) => {
		console.error('!!!! An unrecoverable error exception occurred. The error is', event.error);
		setHasAVPError(true);
	};

	const updateActive = () => {
		setActive3DViews([]);
		setAVPSessionId('');
		setHideAVP(false);
	};

	const streamInitialized = () => {
		setAVPSessionId(buffer);
		setActive3DViews(Object.keys(AvailableAvpViews));
	};

	interface ExceptionEvent extends Event {
		error: Error;
		message: string;
	}

	useEffect(() => {
		document.addEventListener('connected', (event: CustomEventInit) => connectedToAVP(event.detail.sessionId));
		document.addEventListener('streamInitialized', () => streamInitialized());
		document.addEventListener('exception', (event) => handleException(event as ExceptionEvent));
		document.addEventListener('update:active', () => updateActive());
		return () => {
			document.removeEventListener('connected', (event: CustomEventInit) =>
				connectedToAVP(event.detail.sessionId),
			);
			document.removeEventListener('streamInitialized', () => streamInitialized());
			document.removeEventListener('exception', (event) => handleException(event as ExceptionEvent));
			document.removeEventListener('update:active', () => updateActive());
		};
	}, [JSON.stringify(active3DViews), AVPSessionId, buffer]);

	const CtxValue: CtxConfiguratorType = {
		hasAVPError,
		setHasAVPError,

		isThumbnailOpen,
		setIsThumbnailOpen,

		galleryWidth,
		setGalleryWidth,

		galleryHeight,
		setGalleryHeight,

		galleryTop,
		setGalleryTop,

		optionHeaderHeight,
		setOptionHeaderHeight,

		triggerComparisonPosition,
		setTriggerComparisonPosition,

		isPinned,
		setIsPinned,

		totalMatchFound,
		setTotalMatchFound: (_totalMatchFound: number) => {
			_totalMatchFound;
			setTotalMatchFound(Math.floor(Math.random() * 101));
		},

		carWithDealer,
		setCarWithDealer,

		active3DViews,
		setActive3DViews,

		AVPSessionId,
		setAVPSessionId,

		AVPCameraId,
		setAVPCameraId,

		AVPConfigId,
		setAVPConfigId,

		hideAVP,
		setHideAVP,

		openPriceDetails,
		setOpenPriceDetails: (val: boolean) => {
			const body = document.getElementsByTagName('body');
			body[0].style.overflow = openPriceDetails ? 'auto' : 'hidden';
			setOpenPriceDetails(val);
		},
	};

	return <CtxConfigurator.Provider value={CtxValue}>{children}</CtxConfigurator.Provider>;
}
